/**
 * Tracks "Filter Applied" events for Mixpanel
 * Captures when filters/facets are applied or removed in Algolia search
 */

(function () {
  'use strict';

  // Store previous state to detect changes
  let previousState = null;
  let isInitialized = false;
  let hasStoredInitialState = false;

  /**
   * Determines the page type (search result or collection page)
   */
  function getPageType() {
    const pathname = window.location.pathname;

    if (pathname.includes('/search') || pathname === '/search') {
      return 'search_result';
    }

    if (pathname.includes('/collections/')) {
      return 'collection_page';
    }

    // Default fallback
    return 'unknown';
  }

  /**
   * Gets the difference between two filter states
   */
  function getFilterDifference(oldState, newState) {
    const changes = [];

    // Check disjunctiveFacetsRefinements
    const oldDisjunctive = oldState.disjunctiveFacetsRefinements || {};
    const newDisjunctive = newState.disjunctiveFacetsRefinements || {};

    // Check all disjunctive facet attributes
    const allDisjunctiveKeys = new Set([...Object.keys(oldDisjunctive), ...Object.keys(newDisjunctive)]);

    allDisjunctiveKeys.forEach((attribute) => {
      const oldValues = oldDisjunctive[attribute] || [];
      const newValues = newDisjunctive[attribute] || [];
      const oldSet = new Set(oldValues);
      const newSet = new Set(newValues);

      // Find added values
      newValues.forEach((value) => {
        if (!oldSet.has(value)) {
          changes.push({
            filter_name: attribute,
            filter_value: value,
            action: 'applied',
            filter_type: 'disjunctiveFacet',
          });
        }
      });

      // Find removed values
      oldValues.forEach((value) => {
        if (!newSet.has(value)) {
          changes.push({
            filter_name: attribute,
            filter_value: value,
            action: 'removed',
            filter_type: 'disjunctiveFacet',
          });
        }
      });
    });

    // Check facetsRefinements (regular facets)
    const oldFacets = oldState.facetsRefinements || {};
    const newFacets = newState.facetsRefinements || {};

    // Check all facet attributes
    const allFacetKeys = new Set([...Object.keys(oldFacets), ...Object.keys(newFacets)]);

    allFacetKeys.forEach((attribute) => {
      const oldValues = oldFacets[attribute] || [];
      const newValues = newFacets[attribute] || [];
      const oldSet = new Set(oldValues);
      const newSet = new Set(newValues);

      // Find added values
      newValues.forEach((value) => {
        if (!oldSet.has(value)) {
          changes.push({
            filter_name: attribute,
            filter_value: value,
            action: 'applied',
            filter_type: 'facet',
          });
        }
      });

      // Find removed values
      oldValues.forEach((value) => {
        if (!newSet.has(value)) {
          changes.push({
            filter_name: attribute,
            filter_value: value,
            action: 'removed',
            filter_type: 'facet',
          });
        }
      });
    });

    return changes;
  }

  /**
   * Tracks a filter change event
   */
  function trackFilterChange(filterChange) {
    try {
      // Check if Mixpanel is available
      if (typeof window.mixpanel === 'undefined' || !window.mixpanel.track) {
        return;
      }

      // Skip tracking for bots
      if (typeof isBot === 'function' && isBot()) {
        return;
      }

      const pageType = getPageType();

      // Standardize the (potentially localized) facet value to a canonical, language-stable
      // handle; keep the original localized value separately for display/QA.
      const standardizedValue =
        window.MixpanelI18n && typeof window.MixpanelI18n.normalizeFacetValue === 'function'
          ? window.MixpanelI18n.normalizeFacetValue(filterChange.filter_name, filterChange.filter_value)
          : filterChange.filter_value;

      // Track the event
      mixpanel.track('Filters Changed V.1', {
        'Page Type': pageType,
        'Filter Name': filterChange.filter_name,
        'Filter Value': standardizedValue,
        'Filter Value (Display)': filterChange.filter_value,
        Action: filterChange.action,
        'User Agent': navigator.userAgent,
      });
    } catch (error) {
      // Error tracking Filters Changed
    }
  }

  /**
   * Handles Algolia helper state changes
   */
  function handleStateChange(newState) {
    if (!previousState) {
      // First time, just store the state (fallback if initialization didn't work)
      previousState = {
        disjunctiveFacetsRefinements: { ...(newState.disjunctiveFacetsRefinements || {}) },
        facetsRefinements: { ...(newState.facetsRefinements || {}) },
      };
      return;
    }

    // Get the current state
    const currentState = {
      disjunctiveFacetsRefinements: { ...(newState.disjunctiveFacetsRefinements || {}) },
      facetsRefinements: { ...(newState.facetsRefinements || {}) },
    };

    // Find what changed
    const changes = getFilterDifference(previousState, currentState);

    // Track each change
    if (changes.length > 0) {
      changes.forEach((change) => {
        trackFilterChange(change);
      });
    }

    // Update previous state
    previousState = currentState;
  }

  /**
   * Initializes filter tracking
   */
  function initFilterTracking() {
    // Wait for Algolia to be available
    const checkAlgolia = setInterval(() => {
      if (
        window.algoliaShopify &&
        window.algoliaShopify.instantsearch &&
        window.algoliaShopify.instantsearch.search &&
        window.algoliaShopify.instantsearch.search.helper
      ) {
        clearInterval(checkAlgolia);

        const helper = window.algoliaShopify.instantsearch.search.helper;

        // Store initial state immediately
        const initialState = helper.state;
        previousState = {
          disjunctiveFacetsRefinements: {
            ...(initialState.disjunctiveFacetsRefinements || {}),
          },
          facetsRefinements: { ...(initialState.facetsRefinements || {}) },
        };
        hasStoredInitialState = true;
        isInitialized = true;

        // Listen for state changes
        helper.on('change', (event) => {
          if (!hasStoredInitialState) {
            // Store initial state on first change if we haven't already
            previousState = {
              disjunctiveFacetsRefinements: {
                ...(event.state.disjunctiveFacetsRefinements || {}),
              },
              facetsRefinements: { ...(event.state.facetsRefinements || {}) },
            };
            hasStoredInitialState = true;
            return;
          }

          handleStateChange(event.state);
        });
      }
    }, 100);

    // Stop checking after 10 seconds
    setTimeout(() => {
      clearInterval(checkAlgolia);
    }, 10000);
  }

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initFilterTracking);
  } else {
    initFilterTracking();
  }

  // Also listen for Algolia initialization events
  window.addEventListener('algolia.finishedInitialization', function () {
    if (!isInitialized) {
      initFilterTracking();
    }
  });
})();
